import { isSurfaceLanguage, normalizeCulture, type SurfaceLanguage } from './languages'

export interface LocalePreference {
  readonly language?: unknown
  readonly culture?: unknown
}

export interface LocaleSelection {
  readonly language: SurfaceLanguage
  /** Absent means automatic culture; never persist an inferred culture as a preference. */
  readonly culture?: string
}

export interface LanguageCookieOptions {
  readonly name?: string
  readonly domain?: string
  readonly secure?: boolean
}

export interface LocaleCookieEnvironment {
  readonly VITE_LOCALE_COOKIE_NAME?: string
  readonly VITE_LOCALE_COOKIE_DOMAIN?: string
  readonly VITE_LOCALE_COOKIE_SECURE?: string
}

export function localeCookieOptionsFromEnv(env: LocaleCookieEnvironment): LanguageCookieOptions {
  const secure = env.VITE_LOCALE_COOKIE_SECURE
  if (secure !== undefined && secure !== '' && secure !== 'true' && secure !== 'false') {
    throw new RangeError('VITE_LOCALE_COOKIE_SECURE must be true or false')
  }
  return {
    name: env.VITE_LOCALE_COOKIE_NAME || 'fusapp-cosmos-locale',
    domain: env.VITE_LOCALE_COOKIE_DOMAIN || undefined,
    secure: secure !== 'false',
  }
}

function parseHint(value: string): LocaleSelection | undefined {
  try {
    const [language, rawCulture, extra] = decodeURIComponent(value).split(':')
    if (!isSurfaceLanguage(language) || extra !== undefined) return undefined
    if (rawCulture === undefined) return { language }
    const culture = normalizeCulture(rawCulture)
    return culture ? { language, culture } : undefined
  } catch (error) {
    if (error instanceof URIError) return undefined
    throw error
  }
}

/** Only a public language/culture hint. Never contains session or authentication data. */
export function languageCookie(options: LanguageCookieOptions = {}, target?: Pick<Document, 'cookie'>) {
  const name = options.name ?? 'fusapp-cosmos-locale'
  const domain = options.domain
  if (!/^[A-Za-z0-9_.-]+$/.test(name)) throw new RangeError('Invalid locale cookie name')
  if (domain && !/^\.?[a-z\d](?:[a-z\d-]{0,61}[a-z\d])?(?:\.[a-z\d](?:[a-z\d-]{0,61}[a-z\d])?)*$/i.test(domain)) {
    throw new RangeError('Invalid locale cookie domain')
  }
  const cookieDocument = target ?? (typeof document === 'undefined' ? undefined : document)
  const attributes = `; Path=/; Max-Age=31536000; SameSite=Lax${domain ? `; Domain=${domain}` : ''}${options.secure === false ? '' : '; Secure'}`

  return {
    read(): LocalePreference {
      let stored: string
      try {
        stored = cookieDocument?.cookie ?? ''
      } catch (error) {
        console.warn('[surface-locale] Unable to read locale cookie; ignoring browser hint.', error)
        return {}
      }
      for (const part of stored.split(';')) {
        const entry = part.trim()
        if (!entry.startsWith(`${name}=`)) continue
        const hint = parseHint(entry.slice(name.length + 1))
        if (hint) return hint
      }
      return {}
    },
    write(selection: LocaleSelection): void {
      if (!isSurfaceLanguage(selection.language)) throw new RangeError('Unsupported surface language')
      const culture = normalizeCulture(selection.culture)
      if (selection.culture !== undefined && !culture) throw new RangeError('Invalid surface culture')
      const value = `${selection.language}${culture ? `:${culture}` : ''}`
      try {
        if (cookieDocument) cookieDocument.cookie = `${name}=${encodeURIComponent(value)}${attributes}`
      } catch (error) {
        console.warn('[surface-locale] Unable to write locale cookie.', error)
        throw error
      }
    },
  }
}
